export type CommonPostInteractiveQuestionAnswerRequestBody = {
  interactiveQuestionId: string;
  type: TrackingInteractiveQuestionType;
  timestamp: number;
  version: number;
};

export type PostSlideVisitedRequestBody = {
  timestamp: number;
  slideId: string;
  eventId: string;
  time: number;
  startTime: number;
  version: number;
};

type CommonPostQuestionRequestBody = CommonPostInteractiveQuestionAnswerRequestBody & {
  answerIds: string[];
  timeTakenToAnswer: number;
};

export type PostQuizInteractiveQuestionAnswerRequestBody =
  CommonPostQuestionRequestBody & {
    type: TrackingInteractiveQuestionType.Quiz;
  };

export type PostOpenInteractiveQuestionAnswerRequestBody =
  CommonPostInteractiveQuestionAnswerRequestBody & {
    type: TrackingInteractiveQuestionType.OpenAnswer;
    answerText: string;
  };

export type PostTrueFalseInteractiveQuestionRequestBody =
  CommonPostQuestionRequestBody & {
    type: TrackingInteractiveQuestionType.TrueFalse;
  };

export type PostSortInteractiveQuestionRequestBody = CommonPostQuestionRequestBody & {
  type: TrackingInteractiveQuestionType.Sort;
};

export type DeleteUserTrackingRequestBody = {
  geniallyId: string;
  userAliases: string[];
  userIds: string[];
};

export type GetSurveyResultQueryParams = {
  geniallyId: string;
  interactiveQuestionId: string;
};

export type GetOpenQuestionQueryParams = {
  geniallyId: string;
  interactiveQuestionId: string;
};

export type GetUserQuestionAnswersRankingQueryParams = {
  geniallyId: string;
};

export type PostImageInteractiveQuestionRequestBody = CommonPostQuestionRequestBody & {
  type: TrackingInteractiveQuestionType.Image;
};

export type PostShortAnswerInteractiveQuestionRequestBody =
  CommonPostInteractiveQuestionAnswerRequestBody & {
    type: TrackingInteractiveQuestionType.ShortAnswer;
    answerText: string;
    timeTakenToAnswer: number;
  };

export type PostSurveyInteractiveQuestionRequestBody = CommonPostQuestionRequestBody & {
  type: TrackingInteractiveQuestionType.Survey;
};

export type PostFillInTheBlanksInteractiveQuestionRequestBody =
  CommonPostInteractiveQuestionAnswerRequestBody & {
    userAnswer: Record<string, string>;
    timeTakenToAnswer: number;
  } & {
    type: TrackingInteractiveQuestionType.FillInTheBlanks;
  };

export type PostImageSurveyInteractiveQuestionRequestBody =
  CommonPostQuestionRequestBody & {
    type: TrackingInteractiveQuestionType.ImageSurvey;
  };

export type PostInteractiveQuestionAnswerRequestBody =
  | PostQuizInteractiveQuestionAnswerRequestBody
  | PostOpenInteractiveQuestionAnswerRequestBody
  | PostTrueFalseInteractiveQuestionRequestBody
  | PostImageInteractiveQuestionRequestBody
  | PostShortAnswerInteractiveQuestionRequestBody
  | PostSortInteractiveQuestionRequestBody
  | PostSurveyInteractiveQuestionRequestBody
  | PostFillInTheBlanksInteractiveQuestionRequestBody
  | PostImageSurveyInteractiveQuestionRequestBody;

export type PostScoreableInteractiveQuestionAnswerRequestBody = Exclude<
  PostInteractiveQuestionAnswerRequestBody,
  | PostOpenInteractiveQuestionAnswerRequestBody
  | PostSurveyInteractiveQuestionRequestBody
  | PostImageSurveyInteractiveQuestionRequestBody
>;

export const isPostScoreableInteractiveQuestionAnswerRequestBody = (
  requestBody: PostInteractiveQuestionAnswerRequestBody,
): requestBody is PostScoreableInteractiveQuestionAnswerRequestBody => {
  return (
    requestBody.type !== TrackingInteractiveQuestionType.OpenAnswer &&
    requestBody.type !== TrackingInteractiveQuestionType.Survey &&
    requestBody.type !== TrackingInteractiveQuestionType.ImageSurvey
  );
};

export type PostSessionRequestBody = {
  geniallyId: string;
  timestamp: number;
  userAlias: string;
  deviceId: string;
  hash?: string;
  version: number;
};

export type PostAdminWebhooksRequestBody = {
  eventName: string;
  data: Record<string, string>;
};

export type PostApiWebhooksRequestBody = {
  action: string;
  data: Record<string, string>;
};

export type PostTrackingRequestBody =
  | PostSlideVisitedRequestBody
  | PostInteractiveQuestionAnswerRequestBody
  | PostSessionRequestBody
  | DeleteUserTrackingRequestBody
  | PostAdminWebhooksRequestBody
  | PostApiWebhooksRequestBody;

export enum TrackingInteractiveQuestionType {
  Quiz = 1,
  OpenAnswer = 2,
  TrueFalse = 3,
  Image = 4,
  Sort = 5,
  Survey = 6,
  FillInTheBlanks = 7,
  ShortAnswer = 8,
  ImageSurvey = 9,
}

type CommonTrackingDataAnswerResponse = {
  type: TrackingInteractiveQuestionType;
  interactiveQuestionId: string;
};

export type AnswerableDataQuestionsResponse = CommonTrackingDataAnswerResponse & {
  answerIds: string[];
};

export type TrackingDataQuizInteractiveQuestionAnswerResponse =
  AnswerableDataQuestionsResponse & {
    type: TrackingInteractiveQuestionType.Quiz;
  };

export type TrackingDataOpenInteractiveQuestionAnswerResponse =
  CommonTrackingDataAnswerResponse & {
    type: TrackingInteractiveQuestionType.OpenAnswer;
    answerText: string;
  };

export type TrackingDataTrueFalseQuestionResponse = AnswerableDataQuestionsResponse & {
  type: TrackingInteractiveQuestionType.TrueFalse;
};

export type TrackingDataImageQuestionResponse = AnswerableDataQuestionsResponse & {
  type: TrackingInteractiveQuestionType.Image;
};

export type TrackingDataSortQuestionResponse = AnswerableDataQuestionsResponse & {
  type: TrackingInteractiveQuestionType.Sort;
};

export type TrackingDataSurveyQuestionResponse = AnswerableDataQuestionsResponse & {
  type: TrackingInteractiveQuestionType.Survey;
};

export type TrackingDataImageSurveyQuestionResponse = AnswerableDataQuestionsResponse & {
  type: TrackingInteractiveQuestionType.ImageSurvey;
};

export type TrackingDataFillInTheBlanksQuestionResponse =
  CommonTrackingDataAnswerResponse & {
    userAnswer: Record<string, string>;
  } & {
    type: TrackingInteractiveQuestionType.FillInTheBlanks;
  };
export type TrackingDataShortAnswerQuestionResponse = CommonTrackingDataAnswerResponse & {
  answerText: string;
  type: TrackingInteractiveQuestionType.ShortAnswer;
};

export type TrackingDataCommonAnswerResponse =
  | TrackingDataQuizInteractiveQuestionAnswerResponse
  | TrackingDataOpenInteractiveQuestionAnswerResponse
  | TrackingDataTrueFalseQuestionResponse
  | TrackingDataImageQuestionResponse
  | TrackingDataSortQuestionResponse
  | TrackingDataSurveyQuestionResponse
  | TrackingDataFillInTheBlanksQuestionResponse
  | TrackingDataShortAnswerQuestionResponse
  | TrackingDataImageSurveyQuestionResponse;

export type TrackingDataSelectableAnswerResponse = Exclude<
  TrackingDataCommonAnswerResponse,
  TrackingDataOpenInteractiveQuestionAnswerResponse
>;

export const isTrackingDataSelectableAnswerResponse = (
  response: TrackingDataCommonAnswerResponse,
): response is TrackingDataSelectableAnswerResponse => {
  return response.type !== TrackingInteractiveQuestionType.OpenAnswer;
};

export type TrackingDataScoreableAnswerResponse = Exclude<
  TrackingDataCommonAnswerResponse,
  | TrackingDataOpenInteractiveQuestionAnswerResponse
  | TrackingDataSurveyQuestionResponse
  | TrackingDataImageSurveyQuestionResponse
>;

export const isTrackingDataScoreableAnswerResponse = (
  response: TrackingDataCommonAnswerResponse,
): response is TrackingDataScoreableAnswerResponse => {
  return (
    response.type !== TrackingInteractiveQuestionType.OpenAnswer &&
    response.type !== TrackingInteractiveQuestionType.Survey &&
    response.type !== TrackingInteractiveQuestionType.ImageSurvey
  );
};

export type TrackingDataChoosableAnswerResponse = Exclude<
  TrackingDataScoreableAnswerResponse,
  | TrackingDataSortQuestionResponse
  | TrackingDataFillInTheBlanksQuestionResponse
  | TrackingDataShortAnswerQuestionResponse
>;

export const isTrackingDataChoosableAnswerResponse = (
  response: TrackingDataCommonAnswerResponse,
): response is TrackingDataChoosableAnswerResponse => {
  return response.type !== TrackingInteractiveQuestionType.Sort;
};

export type TrackingDataAnswerDatedResponse = TrackingDataCommonAnswerResponse & {
  timestamp: number;
};

export type TrackingDataGeniallyUser = {
  id: string;
  email: string;
  fullName?: string;
};

export type TrackingDataCommonResponse = {
  geniallyId: string;
  userAlias: string;
  firstSessionCreatedAt: number;
  numberOfSessions: number;
  geniallyUser?: TrackingDataGeniallyUser | { id: string };
  visitedSlides: {
    slideId: string;
    timeStart: number;
    timeEnd: number;
    duration: number;
  }[];
  answers: TrackingDataAnswerDatedResponse[];
};

export type GetAllUserTrackingOfGeniallyQueryParams = {
  startDate: string;
  endDate: string;
};

export interface GetAllUserTrackingOfGeniallyRequestParams {
  geniallyId: string;
}

export type GetUserTrackingQueryParams = {
  version: string;
};

export type GetUserTrackingResponse = {
  answers: TrackingDataCommonAnswerResponse[];
  currentSlideId: string | undefined;
};

export type GetAllUserTrackingOfGeniallyResponse = {
  status: 'ok';
  data: TrackingDataCommonResponse[];
};

export type PostResponse = undefined;

export type PostSlideVisitedResponse = PostResponse;

export type PostSessionResponse = {
  userSessionAuthorization: string;
  anonymousUserSessionCreated: boolean;
};

export type PostInteractiveQuestionAnswerResponse = PostResponse;

export type DeleteUserTrackingResponse = PostResponse;

type DeleteTrackingTinybirdDataResponse = {
  deleteUserSessions: number;
};

export type GetSurveyResultResponse = {
  geniallyId: string;
  interactiveQuestionId: string;
  answerId: string;
  totalAnswers: number;
}[];

export type GetImageSurveyResultResponse = {
  geniallyId: string;
  interactiveQuestionId: string;
  answerId: string;
  totalAnswers: number;
}[];

export type GetOpenQuestionAnswerResponse = {
  geniallyUserId: string;
  userAlias: string;
  registeredAt: string;
  answerText: string;
};

export type GetAllOpenQuestionAnswersResponse = GetOpenQuestionAnswerResponse[];

export type GetUserQuestionAnswersRankingResponse = {
  geniallyUserId: string;
  userAlias: string;
  rightAnswers: number;
  wrongAnswers: number;
}[];

export type PostAdminWebhooksResponse = DeleteTrackingTinybirdDataResponse;
export type PostApiWebhooksResponse = PostResponse;

export type GetGeniallyAudienceLimitReachedResponse = string[];

export type GetSurveyAnswerResultQueryParams = {
  geniallyId: string;
  interactiveQuestionId: string;
};

export type GetImageSurveyAnswerResultQueryParams = {
  geniallyId: string;
  interactiveQuestionId: string;
};
